import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQueries } from '@tanstack/react-query';

import { useTagEpisodesData } from './useTagEpisodesData';
import { getIntegrationTags } from '@insights_module/services/megaphone';
import { AQLQuery } from '@insights_module/adc_ql/types';
import {
  INTEGRATION_EPISODE_LIMIT,
  INTEGRATION_QUERY_KEYS,
  INTEGRATION_TYPE_MAPPER,
} from '@insights_module/constants/integrations';
import { IntegrationType, IntegrationRequestType } from '@insights_module/types/Integrations';
import { AudioRespDataType } from '@insights_module/types/Episode';

export type TagType = {
  id: string;
  type: string;
  extra_fields: {
    ad_tag_name: string;
  };
  created_at: string;
  updated_at: string;
  query: {
    id: string;
    display_name: string;
    description: string;
    aql_query: AQLQuery;
    created_at: string;
    updated_at: string;
  };
};

const queryFunction: ({
  type,
  token,
  page_size,
  searchTerm,
}: {
  type: IntegrationRequestType;
  token?: string;
  page_size?: number;
  searchTerm?: string;
}) => Promise<{
  data: TagType[];
  pagination: { page_size: number; token: string | null };
}> = async ({ type, token, page_size = 20, searchTerm = '' }) => {
  const { data, pagination } = await getIntegrationTags({
    type,
    token,
    page_size,
    name: searchTerm,
  });
  return { data, pagination };
};

export const useIntegrationTags = (type: IntegrationType, searchTerm?: string) => {
  const { getTrailingEpisode } = useTagEpisodesData();
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [tags, setTags] = useState<TagType[]>([]);

  const { data, isLoading, error, isFetchingNextPage, fetchNextPage, hasNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: [INTEGRATION_QUERY_KEYS.INFINITE_TAGS, INTEGRATION_TYPE_MAPPER[type], searchTerm],
      queryFn: ({ pageParam }) =>
        queryFunction({ type: INTEGRATION_TYPE_MAPPER[type], token: pageParam, searchTerm }),
      onSettled: () => {
        setIsInitialLoading(false);
      },
      getNextPageParam: ({ pagination }) => {
        const { token } = pagination;
        return token;
      },
      keepPreviousData: true,
      staleTime: 60 * 60 * 1000, // 1h
      enabled: !!type,
    });

  useEffect(() => {
    if (data?.pages) {
      const arr: TagType[] = [];
      data.pages?.forEach((element) => {
        element.data.map((item: TagType) => arr.push(item));
      });
      setTags(arr);
    } else {
      setTags([]);
    }

    if (!isLoading) {
      setIsInitialLoading(false);
    }
  }, [data?.pages, isLoading]);

  const getTagEpisodesData = async (item: { id: string; query: AQLQuery }) => {
    let oldest: AudioRespDataType[] | null = null;

    const latestEpisodeParams = { query: item.query, sortOrder: 'desc' };
    const { data: latestEpisode, total } = await getTrailingEpisode(latestEpisodeParams);

    const oldestEpisodePrams = {
      query: item.query,
      sortOrder: total < INTEGRATION_EPISODE_LIMIT[type] ? 'asc' : 'desc',
      // pageNumber: total < INTEGRATION_EPISODE_LIMIT[type] ? 1 : 1,  TODO: Dule, why was here 10000?
    };

    const { data: oldestEpisode } = await getTrailingEpisode(oldestEpisodePrams);
    oldest = oldestEpisode;

    return { item, latestEpisode: latestEpisode[0], oldestEpisode: oldest[0], total };
  };

  const detailsQueries = useQueries({
    queries: tags.map((tag) => {
      return {
        queryKey: [INTEGRATION_QUERY_KEYS.TAG_DETAILS, { id: tag.id }],
        queryFn: async () => {
          const res = await getTagEpisodesData({ id: tag?.id, query: tag?.query?.aql_query });
          return { ...res, ...tag };
        },
        staleTime: 60 * 60 * 1000, // 1h
        keepPreviousData: true,
        retry: false,
      };
    }),
  });

  const tagsDetails = useMemo(() => {
    return detailsQueries.map(({ data, isLoading }) => {
      return {
        data,
        isLoading,
      };
    });
  }, [detailsQueries]);

  const getTagById = useCallback(
    (id: string | null): TagType | undefined => {
      if (!id) return;

      return tags.find((item) => item.id === id);
    },
    [tags]
  );

  const getTagByQueryId = (id: string | null): TagType | undefined => {
    if (!id) return;

    return tags.find((item) => item.query.id === id);
  };

  return {
    tags,
    tagsDetails,
    isLoading: isLoading || isFetchingNextPage || isInitialLoading,
    error,
    getTagById,
    getTagByQueryId,
    isInitialLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching,
  };
};
